import React, { useState, useRef, createElement } from 'react';
import { DownloadIcon, PrinterIcon, XIcon } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Invoice } from '../../types';
import { Button } from '../../components/ui/Button';
import { useData } from '../../store/DataContext';
import { IssuerHeader } from './IssuerHeader';
import { calculateInvoicePreTaxLine, calculateInvoiceTotals, formatCurrency } from './utils';
type Props = {
  invoice: Invoice;
  onClose: () => void;
};
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const pageWidth = 210;
  const pageHeight = 297;
  const marginTop = 17.78;
  const marginRight = 10.16;
  const marginBottom = 17.78;
  const marginLeft = 12.7;
  const contentWidth = pageWidth - marginLeft - marginRight;
  const contentHeight = pageHeight - marginTop - marginBottom;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const pixelsPerMillimetre = sourceCanvas.width / contentWidth;
  const sliceHeight = Math.floor(contentHeight * pixelsPerMillimetre);
  let cursor = 0;
  let page = 0;
  while (cursor < sourceCanvas.height) {
    const height = Math.min(sliceHeight, sourceCanvas.height - cursor);
    const slice = document.createElement('canvas');
    slice.width = sourceCanvas.width;
    slice.height = height;
    const context = slice.getContext('2d');
    if (!context) break;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, slice.width, slice.height);
    context.drawImage(sourceCanvas, 0, cursor, sourceCanvas.width, height, 0, 0, slice.width, height);
    if (page > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', marginLeft, marginTop, contentWidth, height / pixelsPerMillimetre);
    cursor += height;
    page += 1;
  }
  pdf.save(filename);
}
export function InvoiceModal({
  invoice,
  onClose
}: Props) {
  const {
    patients,
    walkInClients
  } = useData();
  const printableRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const patient = invoice.customerType === 'Patient' ? patients.find(entry => entry.id === invoice.customerId) : undefined;
  const walkIn = invoice.customerType === 'WalkIn' ? walkInClients.find(entry => entry.id === invoice.customerId) : undefined;
  const totals = calculateInvoiceTotals(invoice.items);
  const print = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    window.setTimeout(() => document.body.classList.remove('printing-test-modal'), 500);
  };
  const downloadPdf = async () => {
    if (!printableRef.current) return;
    setIsGeneratingPdf(true);
    try {
      await captureToA4Pdf(printableRef.current, `BMED_${invoice.invoiceNumber}.pdf`);
    } catch (error) {
      console.error('PDF generation failed', error);
      window.alert('Failed to generate the PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 print:bg-white print:p-0" data-id="element-1241">
      <div className="test-modal-print max-h-[95vh] w-full max-w-5xl overflow-y-auto rounded-lg bg-white shadow-2xl print:max-h-none print:max-w-none print:overflow-visible print:rounded-none print:shadow-none" data-id="element-1242">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3 print:hidden" data-id="element-1243">
          <h3 className="text-lg font-semibold text-slate-900" data-id="element-1244">
            Invoice {invoice.invoiceNumber}
          </h3>
          <div className="flex items-center gap-2" data-id="element-1245">
            <Button variant="outline" size="sm" onClick={print} data-id="element-1246">
              <PrinterIcon className="mr-1 h-4 w-4" data-id="element-1247" /> Print
            </Button>
            <Button size="sm" onClick={downloadPdf} disabled={isGeneratingPdf} data-id="element-1248">
              <DownloadIcon className="mr-1 h-4 w-4" data-id="element-1249" />
              {isGeneratingPdf ? 'Generating…' : 'Download PDF'}
            </Button>
            <button type="button" onClick={onClose} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label="Close invoice" data-id="element-1250">
              <XIcon className="h-5 w-5" data-id="element-1251" />
            </button>
          </div>
        </div>

        <div ref={printableRef} className="billing-document bg-white p-0 text-slate-950" data-id="element-1252">
          <header className="flex flex-col gap-6 border-b-2 border-slate-900 pb-5 sm:flex-row sm:items-start sm:justify-between" data-id="element-1253">
            <IssuerHeader data-id="element-1254" />
            <div className="text-left sm:text-right" data-id="element-1255">
              <h1 className="text-2xl font-bold tracking-[0.16em] text-slate-800 sm:text-3xl" data-id="element-1256">
                INVOICE
              </h1>
              <p className="mt-3" data-id="element-1257">
                <span className="font-semibold" data-id="element-1258">Invoice #:</span>{' '}
                {invoice.invoiceNumber}
              </p>
              <p data-id="element-1259">
                <span className="font-semibold" data-id="element-1260">Date:</span>{' '}
                {new Date(invoice.date).toLocaleDateString()}
              </p>
              <p data-id="element-1261">
                <span className="font-semibold" data-id="element-1262">Due:</span>{' '}
                {new Date(invoice.dueDate).toLocaleDateString()}
              </p>
            </div>
          </header>

          <section className="my-8 flex justify-between gap-8" data-id="element-1263">
            <div data-id="element-1264">
              <h3 className="mb-2 font-bold uppercase text-slate-500" data-id="element-1265">
                Bill To
              </h3>
              {invoice.customerType === 'Patient' ? <>
                  <p className="text-lg font-bold" data-id="element-1266">
                    {patient ? `${patient.firstName} ${patient.lastName}` : 'Registered patient'}
                  </p>
                  {patient && <>
                      <p data-id="element-1267">File No: {patient.bmcFileNumber}</p>
                      <p data-id="element-1268">{patient.address}</p>
                      <p data-id="element-1269">{patient.contact}</p>
                    </>}
                </> : <>
                  <p className="text-lg font-bold" data-id="element-1270">
                    {invoice.customerName || walkIn?.name || 'Walk-in client'}
                  </p>
                  {walkIn && <>
                      <p data-id="element-1271">{walkIn.address}</p>
                      <p data-id="element-1272">{walkIn.phone}</p>
                    </>}
                </>}
            </div>
            <div className="text-right" data-id="element-1273">
              <h3 className="mb-2 font-bold uppercase text-slate-500" data-id="element-1274">
                Payment Status
              </h3>
              <p className="text-xl font-bold uppercase" data-id="element-1275">{invoice.status}</p>
              <p className="mt-2" data-id="element-1276">
                <span className="font-semibold" data-id="element-1277">Amount Paid:</span>{' '}
                {formatCurrency(invoice.amountPaid)}
              </p>
              <p className="text-red-600" data-id="element-1278">
                <span className="font-semibold" data-id="element-1279">Balance Due:</span>{' '}
                {formatCurrency(invoice.balanceDue)}
              </p>
            </div>
          </section>

          <div className="overflow-x-auto" data-id="element-1280">
            <table className="mb-8 min-w-[760px] w-full border-collapse" data-id="element-1281">
              <thead data-id="element-1282">
                <tr className="border-b-2 border-slate-300 bg-slate-100" data-id="element-1283">
                  <th className="p-2.5 text-left font-bold uppercase text-slate-600" data-id="element-1284">
                    Service
                  </th>
                  <th className="p-2.5 text-right font-bold uppercase text-slate-600" data-id="element-1285">
                    Unit Price
                  </th>
                  <th className="p-2.5 text-right font-bold uppercase text-slate-600" data-id="element-1286">
                    Discount
                  </th>
                  <th className="p-2.5 text-right font-bold uppercase text-slate-600" data-id="element-1287">
                    Pre-tax Total
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1288">
                {invoice.items.map(item => {
                const line = calculateInvoicePreTaxLine(item);
                return <tr key={item.id} className="border-b border-slate-200" data-id="element-1289">
                      <td className="p-2.5" data-id="element-1290">
                        <p className="font-semibold" data-id="element-1291">{item.serviceName}</p>
                        <p className="mt-0.5 text-xs text-slate-500" data-id="element-1292">
                          Qty {item.quantity}
                          {item.description ? ` · ${item.description}` : ''}
                        </p>
                      </td>
                      <td className="p-2.5 text-right" data-id="element-1293">
                        {formatCurrency(item.unitPrice)}
                      </td>
                      <td className="p-2.5 text-right" data-id="element-1294">
                        {item.discount}%
                        <span className="block text-xs text-red-600" data-id="element-1295">
                          -{formatCurrency(line.discountAmount)}
                        </span>
                      </td>
                      <td className="p-2.5 text-right font-semibold" data-id="element-1296">
                        {formatCurrency(line.total)}
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between gap-8" data-id="element-1297">
            <div className="max-w-md flex-1" data-id="element-1298">
              {invoice.notes && <>
                  <h4 className="mb-2 font-bold uppercase text-slate-500" data-id="element-1299">
                    Notes
                  </h4>
                  <p className="whitespace-pre-wrap text-slate-700" data-id="element-1300">
                    {invoice.notes}
                  </p>
                </>}
              {invoice.paymentTerms && <div className="mt-5" data-id="element-1301">
                  <h4 className="mb-2 font-bold uppercase text-slate-500" data-id="element-1302">
                    Terms
                  </h4>
                  <p className="text-slate-700" data-id="element-1303">{invoice.paymentTerms}</p>
                </div>}
            </div>
            <dl className="w-full max-w-xs" data-id="element-1304">
              <div className="flex justify-between py-1" data-id="element-1305">
                <dt className="text-slate-600" data-id="element-1306">Subtotal:</dt>
                <dd className="font-medium" data-id="element-1307">
                  {formatCurrency(totals.subtotal)}
                </dd>
              </div>
              {totals.totalDiscount > 0 && <div className="flex justify-between py-1" data-id="element-1308">
                  <dt className="text-slate-600" data-id="element-1309">Discount:</dt>
                  <dd className="font-medium text-red-600" data-id="element-1310">
                    -{formatCurrency(totals.totalDiscount)}
                  </dd>
                </div>}
              <div className="flex justify-between border-b border-slate-300 py-1" data-id="element-1311">
                <dt className="text-slate-600" data-id="element-1312">TOT Tax (5%):</dt>
                <dd className="font-medium" data-id="element-1313">
                  {formatCurrency(totals.totalTax)}
                </dd>
              </div>
              <div className="flex justify-between py-3 text-lg font-bold" data-id="element-1314">
                <dt data-id="element-1315">Total:</dt>
                <dd className="text-blue-700" data-id="element-1316">
                  {formatCurrency(totals.totalAmount)}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>;
}